import React, {
  useState,
  useMemo,
  memo,
  useCallback,
  useEffect,
  lazy,
  Suspense,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import Icon from "../../assets/icons/Icon";

// Dynamic import for SearchInput
const SearchInput = lazy(() => import("../UI/SearchInput"));

// Loading spinner component
const LoadingSpinner = () => (
  <svg width="30" height="60" viewBox="0 0 100 100">
    <circle
      cx="50"
      cy="50"
      r="40"
      stroke="var(--font_lists_gray_color)"
      strokeWidth="10"
      fill="none"
    />
    <circle
      cx="50"
      cy="50"
      r="40"
      stroke="var(--blue-color)"
      strokeWidth="10"
      fill="none"
      strokeLinecap="round"
      strokeDasharray="188"
      strokeDashoffset="140"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 50 50"
        to="360 50 50"
        dur="1s"
        repeatCount="indefinite"
      />
    </circle>
  </svg>
);

const DataTable = ({
  columns,
  fetchData,
  currentPage = 1,
  onPageChange,
  onRecordsPerPageChange,
  recordsPerPage = 50,
  searchTerm = "",
  onSearchChange,
  isSearchable = true,
  isPagination = true,
  isPageSize = true,
  isLoading = false,
  handleSearchKey,
  isCheckBox = false,
  isIndexing = true,
  onSelectionChange,
  className = "",
  isQuickSearch = false,
}) => {
  const { data = [], pages = 0 } =
    fetchData({
      searchTerm,
      recordsPerPage,
      currentPage,
    }) || {};

  const [sorting, setSorting] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [globalFilter, setGlobalFilter] = useState("");

  // Update parent state using onSelectionChange after render
  useEffect(() => {
    if (onSelectionChange) {
      const selectedIds = Array.from(selectedRows);
      onSelectionChange(selectedIds);
    }
  }, [selectedRows, onSelectionChange]);

  // Memoized handlers
  const handleSelectAllChange = useCallback(
    (e) => {
      if (e.target.checked) {
        const allRowIds = data.map((row) => row._id);
        setSelectedRows(new Set(allRowIds));
      } else {
        setSelectedRows(new Set());
      }
    },
    [data],
  );

  const handleRowSelect = useCallback((rowId) => {
    setSelectedRows((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
      } else {
        newSelected.add(rowId);
      }
      return newSelected;
    });
  }, []);

  const handleRecordsPerPageChange = useCallback(
    (event) => {
      onRecordsPerPageChange(Number(event.target.value));
      onPageChange(1);
    },
    [onRecordsPerPageChange, onPageChange],
  );

  const handleSearch = useCallback(
    (val) => {
      onSearchChange(val);
      onPageChange(1);
    },
    [onSearchChange, onPageChange],
  );

  const handleHeaderClick = useCallback((column) => {
    column.toggleSorting(column.getIsSorted() === "asc");
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      onPageChange(page);
    },
    [onPageChange],
  );

  // Memoized values
  const isHeaderChecked = useMemo(
    () => data.length > 0 && selectedRows.size === data.length,
    [selectedRows.size, data.length],
  );

  const indexedColumns = useMemo(() => {
    const columnsWithIndex = isIndexing
      ? [
          {
            id: "index",
            header: "#",
            size: 10,
            cell: ({ row }) =>
              row.index + 1 + (currentPage - 1) * recordsPerPage,
          },
        ]
      : [];

    const columnsWithCheckbox = isCheckBox
      ? [
          {
            id: "checkbox",
            header: (
              <input
                type="checkbox"
                checked={isHeaderChecked}
                onChange={handleSelectAllChange}
              />
            ),
            cell: ({ row }) => (
              <input
                type="checkbox"
                checked={selectedRows.has(row.original._id)}
                onChange={() => handleRowSelect(row.original._id)}
              />
            ),
          },
        ]
      : [];

    return [...columnsWithIndex, ...columnsWithCheckbox, ...columns];
  }, [
    columns,
    currentPage,
    recordsPerPage,
    isIndexing,
    isCheckBox,
    isHeaderChecked,
    selectedRows,
    handleSelectAllChange,
    handleRowSelect,
  ]);

  const table = useReactTable({
    columns: indexedColumns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
  });

  const renderPageNumbers = useMemo(() => {
    const visiblePages = 5;
    const pagesToShow = [];

    let startPage = Math.max(currentPage - Math.floor(visiblePages / 2), 1);
    let endPage = Math.min(startPage + visiblePages - 1, pages);

    if (endPage - startPage + 1 < visiblePages) {
      startPage = Math.max(endPage - visiblePages + 1, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pagesToShow.push(
        <button
          key={i}
          className={`btn ${
            i === currentPage ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>,
      );
    }

    return pagesToShow;
  }, [currentPage, pages, handlePageChange]);

  const recordsPerPageOptions = useMemo(() => [50, 75, 100, 200], []);

  return (
    <>
      {/* Search and Records Per Page Controls */}
      {(isPageSize || isSearchable || isQuickSearch) && (
        <div className="d-flex justify-content-between ">
          {isPageSize && (
            <select
              className="form-select table_curt_select"
              value={recordsPerPage}
              onChange={handleRecordsPerPageChange}
            >
              {recordsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}

          {isSearchable && (
            <Suspense fallback={<div>Loading search...</div>}>
              <SearchInput
                onSearchChange={handleSearch}
                handleSearchKey={handleSearchKey}
              />
            </Suspense>
          )}

          {isQuickSearch && (
            <>
              <div className="input_search_bar">
                <Icon name="Search" className="search_icon" />
                <input
                  type="text"
                  placeholder="Quick search..."
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="form-control"
                />

                {globalFilter ? (
                  <Icon
                    name="X"
                    className="close_icon"
                    onClick={() => setGlobalFilter("")}
                  />
                ) : null}
              </div>
            </>
          )}
        </div>
      )}

      {/* Table */}
      <div className={`recentOrders desktop_ui mt-2 ${className}`}>
        <table className="report-card-box">
          <thead className="table-light">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={() => handleHeaderClick(header.column)}
                    style={{ cursor: "pointer", width: header.getSize() }}
                    className={header.column.columnDef.headerClassName || ""}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {header.column.getIsSorted() === "asc" ? " ▲" : ""}
                    {header.column.getIsSorted() === "desc" ? " ▼" : ""}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={indexedColumns.length} className="text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={indexedColumns.length} className="text-center">
                  No records found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef.className || ""}
                      style={{ width: cell.column.getSize() }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {isPagination && !isLoading && data.length > 0 && (
        <div className="pagination page_ageChange_box">
          {/* First and Previous */}
          <button
            className="btn btn-outline-primary"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            &lt;
          </button>

          {/* Page Numbers */}
          {renderPageNumbers}

          {/* Next and Last */}
          <button
            className="btn btn-outline-primary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pages}
          >
            &gt;
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => handlePageChange(pages)}
            disabled={currentPage === pages}
          >
            Last
          </button>
        </div>
      )}
    </>
  );
};

export default memo(DataTable);
